"use client";

import * as React from "react";
import { type DialogProps } from "@radix-ui/react-dialog";
import dynamic from "next/dynamic";
import { useLenis } from "lenis/react";
import { useChatBot } from "@/components/chat-bot-context";

// The dialog body (cmdk + radix) is code-split — only fetched once the menu is
// first opened. The trigger button and the Cmd-K / "/" listener stay eager.
const CommandPalette = dynamic(() => import("./command-palette"), {
  ssr: false,
});

export function CommandMenu({ ...props }: DialogProps) {
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState("");
  // Flips true the first time the menu opens; keeps CommandPalette mounted
  // afterwards so radix can still play its close animation.
  const [primed, setPrimed] = React.useState(false);
  const { openWithPrompt } = useChatBot();
  const lenis = useLenis();

  React.useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if ((e.key === "k" && (e.metaKey || e.ctrlKey)) || e.key === "/") {
        const t = e.target;
        if (
          (t instanceof HTMLElement && t.isContentEditable) ||
          t instanceof HTMLInputElement ||
          t instanceof HTMLTextAreaElement ||
          t instanceof HTMLSelectElement
        ) {
          return;
        }
        e.preventDefault();
        setPrimed(true);
        setOpen((o) => !o);
      }
    };
    document.addEventListener("keydown", down);
    return () => document.removeEventListener("keydown", down);
  }, []);

  return (
    <>
      <button
        type="button"
        onClick={() => {
          setPrimed(true);
          setOpen(true);
        }}
        className="inline-flex h-8 items-center gap-2 rounded-full border border-hairline px-3 text-xs text-fg-muted transition-colors hover:border-hairline-strong hover:text-fg"
        {...(props as React.ComponentProps<"button">)}
      >
        <span>Search</span>
        <kbd className="font-mono text-[10px] text-fg-faint">⌘K</kbd>
      </button>

      {primed && (
        <CommandPalette
          open={open}
          onOpenChange={setOpen}
          query={query}
          setQuery={setQuery}
          lenis={lenis}
          openWithPrompt={openWithPrompt}
        />
      )}
    </>
  );
}
